import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { geocodeAPI } from '../../lib/api';

export default function AddressAutocomplete({
  value,
  onAddressChange,
  onSelect,
  placeholder = 'Start typing address...'
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const q = query.trim();

    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await geocodeAPI.suggestions(q);
        setSuggestions(res.data || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [query]);

  const handleChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    onAddressChange?.(next);
  };

  const handleSelect = (item) => {
    let selectedAddress = item.label || item.address || '';

    const typed = (query || '').trim();
    const typedNumber = typed.match(/^\\d+[A-Za-z]?/);
    const selectedStartsWithNumber = /^\\d+[A-Za-z]?\\b/.test(selectedAddress.trim());

    if (typedNumber && !selectedStartsWithNumber) {
      selectedAddress = `${typedNumber[0]} ${selectedAddress}`;
    }

    setQuery(selectedAddress);
    setOpen(false);
    setSuggestions([]);
    onSelect?.({
      address: selectedAddress,
      latitude: item.latitude,
      longitude: item.longitude,
    });
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={query}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid="visit-address-input"
      />

      {open && (
        <div
          className="
            absolute left-0 right-0 top-full z-[9999] mt-1
            max-h-72 overflow-y-auto rounded-xl
            border border-slate-700
            bg-slate-950 text-slate-100
            shadow-2xl
          "
        >
          {loading && (
            <div className="px-4 py-3 text-sm text-slate-300">
              Searching addresses...
            </div>
          )}

          {!loading && suggestions.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-300">
              No matching addresses found.
            </div>
          )}

          {!loading && suggestions.map((item, index) => {
            const label = item.label || item.address || '';
            const title = label.split(',')[0];

            return (
              <button
                key={`${label}-${index}`}
                type="button"
                onClick={() => handleSelect(item)}
                className="
                  block w-full border-b border-slate-800 px-4 py-3
                  text-left transition-colors last:border-b-0
                  hover:bg-slate-800 focus:bg-slate-800 focus:outline-none
                "
              >
                <div className="font-semibold text-slate-50">
                  {title}
                </div>
                <div className="mt-1 text-xs leading-snug text-slate-300">
                  {label}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
